/**
 * Login Page
 * Email-only magic link flow - enter email, check inbox
 */
import { useState, type FormEvent } from "react";
import { sendMagicLink } from "../lib/auth";

export default function Login() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await sendMagicLink(email);
      setEmailSent(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to send magic link",
      );
    } finally {
      setIsLoading(false);
    }
  }

  // Show "check your inbox" state after email is sent
  if (emailSent) {
    return (
      <div className="min-h-screen bg-(--color-background) flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-(--color-background) border border-(--color-border) rounded-lg shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-(--color-foreground) mb-2">
              Check your inbox
            </h1>
            <p className="text-(--color-muted-foreground) mb-6">
              We sent a sign-in link to{" "}
              <span className="font-medium text-(--color-foreground)">
                {email}
              </span>
            </p>
            <p className="text-sm text-(--color-muted-foreground) mb-4">
              Click the link in the email to sign in. The link expires in 10
              minutes.
            </p>
            <button
              onClick={() => {
                setEmailSent(false);
                setEmail("");
              }}
              className="text-sm text-(--color-primary) hover:underline"
            >
              Use a different email
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-(--color-background) flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-(--color-background) border border-(--color-border) rounded-lg shadow-sm p-8">
          <h1 className="text-2xl font-bold text-(--color-foreground) text-center mb-2">
            Sign in to NovelReader
          </h1>
          <p className="text-(--color-muted-foreground) text-center mb-6">
            Enter your email and we'll send you a sign-in link
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-(--color-foreground) mb-1"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full px-3 py-2 border border-(--color-border) rounded-md bg-(--color-background) text-(--color-foreground) placeholder-(--color-muted-foreground) focus:outline-none focus:ring-2 focus:ring-(--color-primary) focus:border-transparent"
                placeholder="you@example.com"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2 px-4 bg-(--color-primary) text-(--color-primary-foreground) font-medium rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Sending link..." : "Send sign-in link"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
